import React, { memo, useState } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import { Link } from 'react-router-dom'
import { Card } from 'primereact/card'
import { Column } from 'primereact/column'
import { Button } from 'primereact/button'
import { PrimeIcons } from 'primereact/api'

import SettingsInfo from '../../../ui/chargepoints/SettingsInfo'
import {
    ChargePoint,
    useChargePointAddToPoolMutation,
    usePoolGetChargePointsQuery,
} from '../../../common/redux/api/web-api'
import useText from '../../../common/hooks/useText'
import { Table } from '../../../common/uikit/Table'
import { Input } from '../../../common/uikit/Input'
import { useToast } from '../../../common/uikit/ToastProvider'
import { ChargepointStatus } from '../../../lib/chargepoint-status'
import localizeDate from '../../../common/utils/localizeDate'
import { getChargerProductModelName } from '../../../common/enums/Products'

import { AddChargePointToPoolDialog } from './dialogs/add-charge-point-to-pool-dialog'

export type AdminCompanyChargersWidgetProps = {
    className?: ClassName
    style?: React.CSSProperties
    poolId: string
}

export const AdminCompanyChargersWidget = memo(function AdminCompanyChargersWidget({
    className,
    style,
    poolId,
}: AdminCompanyChargersWidgetProps): React.ReactElement {
    const text = useText()
    const toast = useToast()

    const [addChargersDialog, setAddChargersDialog] = useState({ visible: false, sessionId: 0 })
    const [pendingChargePointIds, setPendingChargePointIds] = useState<Set<string>>(() => new Set())

    const [globalFilter, setGlobalFilter] = useState('')

    const openAddChargersDialog = () => {
        setAddChargersDialog(({ sessionId }) => ({ visible: true, sessionId: sessionId + 1 }))
    }

    const hideAddChargersDialog = () => {
        setAddChargersDialog((dialog) => ({ ...dialog, visible: false }))
    }

    const { data: chargePointsData, isFetching: isLoading } = usePoolGetChargePointsQuery(
        {
            poolId,
        },
        { refetchOnMountOrArgChange: true }
    )

    const [addChargePointToPool] = useChargePointAddToPoolMutation()
    const handleAddChargerToPool = async (chargePoint: ChargePoint) => {
        setPendingChargePointIds((ids) => new Set(ids).add(chargePoint.id))

        try {
            await addChargePointToPool({ chargePointId: chargePoint.id, poolId }).unwrap()
            toast.notifySuccess(text('lblChargerWasAddedToPool'))
        } catch {
            toast.notifyError(text('msgChargerFailedToAddToPool'))
        } finally {
            setPendingChargePointIds((ids) => {
                const nextIds = new Set(ids)
                nextIds.delete(chargePoint.id)
                return nextIds
            })
        }
    }

    return (
        <Card className={cn(className)} style={style}>
            <header className="flex flex-column md:flex-row md:align-items-center justify-content-between gap-3 mb-4">
                <h2 className="p-card-title m-0">{text('lblChargers')}</h2>

                <div className="flex gap-2">
                    <Button
                        data-testid="table-rfid-add-btn"
                        onClick={openAddChargersDialog}
                        label={text('btnAdd')}
                        icon="pi pi-plus"
                    />

                    <Input
                        id="chargers-search-input"
                        type="search"
                        value={globalFilter}
                        onChange={(e) => setGlobalFilter(e.target.value)}
                        placeholder={text('lblSearch')}
                        icon={PrimeIcons.SEARCH}
                    />
                </div>
            </header>

            <AddChargePointToPoolDialog
                key={addChargersDialog.sessionId}
                visible={addChargersDialog.visible}
                pendingChargePointIds={pendingChargePointIds}
                onAdd={handleAddChargerToPool}
                onHide={hideAddChargersDialog}
            />

            <Table
                value={chargePointsData ?? []}
                lazy
                paginator
                globalFilter={globalFilter}
                globalFilterFields={['id', 'name']}
                totalRecords={chargePointsData?.length}
                loading={isLoading}
                emptyMessage={text('lblNoChargersFound')}
                data-testid="table-charge-points"
            >
                <Column
                    field="id"
                    header={text('lblSerialNumberColumn')}
                    body={(chargePoint) => (
                        <Link
                            className="m-0"
                            data-testid={`tb-${chargePoint.id}`}
                            to={`/chargepoints/${chargePoint.id}`}
                        >
                            {chargePoint.id}
                        </Link>
                    )}
                ></Column>
                <Column field="name" header={text('lblName')}></Column>
                <Column
                    field="productType"
                    header={text('lblModel')}
                    body={(chargePoint) =>
                        getChargerProductModelName(chargePoint.productId, chargePoint.productType ?? '')
                    }
                ></Column>
                <Column
                    field="status"
                    header={text('lblStatus')}
                    body={(chargePoint) => <ChargepointStatus chargePoint={chargePoint} />}
                ></Column>
                <Column
                    field="lastSeen"
                    header={text('lblLastSeenTime')}
                    body={(chargePoint) =>
                        chargePoint.chargePointStatus === 'Offline'
                            ? localizeDate(chargePoint?.lastHeartbeat)
                            : undefined
                    }
                ></Column>
                <Column
                    field="settings"
                    header={text('lblSettings')}
                    body={(chargePoint) => <SettingsInfo chargePoint={chargePoint} />}
                ></Column>
                <Column field="additionalInfo.partnerDescription" header={text('lblPartnerInfo')}></Column>
            </Table>
        </Card>
    )
})
